/**
 * PATH: packages/ui/src/components/settings/entities-tab/EntityStructurePanel.tsx
 * PURPOSE: Corporate structure mode — group tree + ultimate shareholders.
 */

import { useCallback, useEffect, useState } from 'react'
import { Building2, Crown, Loader2, Network, Plus, Trash2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { SettingsApiClient } from '../types'
import type {
  EntityShareholder,
  EntityStructureNode,
  EntityStructureResponse,
  OrganisationEntity,
} from './entities-tab-types'
import { entityKindLabel, flattenStructureEntities, parentOptions } from './buildEntityTree'

export interface EntityStructurePanelProps {
  apiClient: SettingsApiClient
  organisationId: string
  entities: OrganisationEntity[]
  onRefreshEntities: () => Promise<void>
}

const KINDS = [
  { value: 'holding', label: 'Holding company' },
  { value: 'operating', label: 'Operating company' },
  { value: 'mixed', label: 'Holding + operating' },
] as const

export function EntityStructurePanel({
  apiClient,
  organisationId,
  entities,
  onRefreshEntities,
}: EntityStructurePanelProps) {
  const [data, setData] = useState<EntityStructureResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const fetchStructure = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get(`/organisations/${organisationId}/entities/structure/`)
      const body = (res?.data ?? res) as EntityStructureResponse
      setData(body)
      if (!selectedId && body.entities?.length) {
        setSelectedId(body.ultimate_holding_id ?? body.entities[0].id)
      }
    } catch {
      setError('Unable to load corporate structure')
    } finally {
      setLoading(false)
    }
  }, [apiClient, organisationId, selectedId])

  useEffect(() => {
    void fetchStructure()
  }, [fetchStructure])

  const selected = data?.entities.find((e) => e.id === selectedId) ?? null
  const ultimateId = data?.ultimate_holding_id ?? null

  const patchEntity = async (patch: Record<string, unknown>) => {
    if (!selectedId) return
    setSaving(true)
    setError(null)
    try {
      await apiClient.patch?.(
        `/organisations/${organisationId}/entities/${selectedId}/structure/`,
        patch,
      )
      await fetchStructure()
      await onRefreshEntities()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: Record<string, string[]> } })?.response?.data
      const first = msg && Object.values(msg)[0]?.[0]
      setError(first || 'Unable to save structure')
    } finally {
      setSaving(false)
    }
  }

  const addShareholder = async () => {
    if (!ultimateId) {
      setError('Mark an entity as Ultimate holding company first')
      return
    }
    setSaving(true)
    try {
      await apiClient.post(`/organisations/${organisationId}/entities/${ultimateId}/shareholders/`, {
        name: 'New shareholder',
        shareholder_kind: 'individual',
        ownership_percent: 0,
        is_ultimate_shareholder: true,
      })
      await fetchStructure()
    } catch {
      setError('Unable to add shareholder')
    } finally {
      setSaving(false)
    }
  }

  const updateShareholder = async (row: EntityShareholder, patch: Partial<EntityShareholder>) => {
    setSaving(true)
    try {
      await apiClient.patch?.(
        `/organisations/${organisationId}/entities/${row.entity}/shareholders/${row.id}/`,
        patch,
      )
      await fetchStructure()
    } catch {
      setError('Unable to update shareholder')
    } finally {
      setSaving(false)
    }
  }

  const deleteShareholder = async (row: EntityShareholder) => {
    setSaving(true)
    try {
      await apiClient.delete?.(
        `/organisations/${organisationId}/entities/${row.entity}/shareholders/${row.id}/`,
      )
      await fetchStructure()
    } catch {
      setError('Unable to delete shareholder')
    } finally {
      setSaving(false)
    }
  }

  const flat = data?.tree ? flattenStructureEntities(data.tree) : []

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Model your group: set parent companies, mark the ultimate holding entity (e.g. Phoenix),
        then record ultimate shareholders on that entity.
      </p>

      {error && (
        <div className="p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading structure…
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Network className="w-4 h-4 text-teal-600" />
              <h4 className="font-medium text-slate-900 dark:text-white">Group structure</h4>
            </div>
            <div className="space-y-1">
              {flat.map(({ node, depth }) => (
                <button
                  key={node.id}
                  type="button"
                  onClick={() => setSelectedId(node.id)}
                  className={cn(
                    'w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors',
                    selectedId === node.id
                      ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                  )}
                  style={{ paddingLeft: 8 + depth * 20 }}
                >
                  <Building2 className="w-4 h-4 shrink-0" style={{ color: node.color || '#6366f1' }} />
                  <span className="font-medium truncate">{node.name}</span>
                  {node.is_ultimate_holding && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                      Ultimate holding
                    </span>
                  )}
                  {node.parent_entity_id && node.ownership_percent != null && (
                    <span className="text-xs text-slate-400 ml-auto">{node.ownership_percent}%</span>
                  )}
                </button>
              ))}
              {flat.length === 0 && (
                <p className="text-sm text-slate-500">No entities - add entities in Xero mapping mode first.</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {selected ? (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-4">
                <h4 className="font-medium text-slate-900 dark:text-white">{selected.name}</h4>
                <label className="block text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Parent company</span>
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                    value={selected.parent_entity_id ?? ''}
                    disabled={saving}
                    onChange={(e) => {
                      const v = e.target.value
                      void patchEntity({ parent_entity_id: v ? Number(v) : null })
                    }}
                  >
                    <option value=""> -  None (top of group)  - </option>
                    {parentOptions(entities, selected.id).map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Entity role</span>
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                    value={selected.entity_kind || 'operating'}
                    disabled={saving}
                    onChange={(e) => void patchEntity({ entity_kind: e.target.value })}
                  >
                    {KINDS.map((k) => (
                      <option key={k.value} value={k.value}>{k.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">{entityKindLabel(selected.entity_kind)}</p>
                </label>
                <label className="block text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Ownership of parent (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                    value={selected.ownership_percent ?? ''}
                    disabled={saving || !selected.parent_entity_id}
                    onChange={(e) => {
                      const v = e.target.value
                      void patchEntity({ ownership_percent: v === '' ? null : Number(v) })
                    }}
                  />
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selected.is_ultimate_holding}
                    disabled={saving}
                    onChange={(e) => void patchEntity({ is_ultimate_holding: e.target.checked })}
                  />
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span>Ultimate holding company</span>
                </label>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Select an entity in the tree to edit its place in the group.</p>
            )}

            {ultimateId && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900 dark:text-white">Ultimate shareholders</h4>
                  <button
                    type="button"
                    onClick={() => void addShareholder()}
                    disabled={saving}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Cap table on{' '}
                  <span className="font-medium">{data?.entities.find((e) => e.id === ultimateId)?.name}</span>
                  {' - '}
                  total {data?.shareholders_total_percent ?? 0}%
                  {data?.shareholders_complete === false && (
                    <span className="text-amber-600 dark:text-amber-400"> (should equal 100%)</span>
                  )}
                </p>
                <div className="space-y-2">
                  {(data?.shareholders ?? []).map((sh) => (
                    <div
                      key={sh.id}
                      className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-slate-100 dark:border-slate-800"
                    >
                      <input
                        className="flex-1 min-w-[120px] rounded border border-slate-200 dark:border-slate-700 px-2 py-1 text-sm bg-white dark:bg-slate-950"
                        value={sh.name}
                        disabled={saving}
                        onChange={(e) => void updateShareholder(sh, { name: e.target.value })}
                      />
                      <select
                        className="rounded border border-slate-200 dark:border-slate-700 px-2 py-1 text-xs bg-white dark:bg-slate-950"
                        value={sh.shareholder_kind}
                        disabled={saving}
                        onChange={(e) => void updateShareholder(sh, { shareholder_kind: e.target.value as EntityShareholder['shareholder_kind'] })}
                      >
                        <option value="individual">Individual</option>
                        <option value="company">Company</option>
                        <option value="trust">Trust</option>
                        <option value="partnership">Partnership</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        className="w-16 rounded border border-slate-200 dark:border-slate-700 px-2 py-1 text-sm bg-white dark:bg-slate-950"
                        value={sh.ownership_percent}
                        disabled={saving}
                        onChange={(e) => void updateShareholder(sh, { ownership_percent: Number(e.target.value) })}
                      />
                      <span className="text-xs text-slate-400">%</span>
                      <button
                        type="button"
                        title="Remove"
                        disabled={saving}
                        onClick={() => void deleteShareholder(sh)}
                        className="p-1 text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(data?.shareholders?.length ?? 0) === 0 && (
                    <p className="text-sm text-slate-500">No shareholders recorded yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
